/**
 * Shared timeline for tutor/student/parent history tabs.
 * Events come from the user_histories backend table.
 */

import * as React from 'react';
import {
  Clock,
  CalendarDays,
  User,
  TrendingUp,
  RefreshCw,
  FolderOpen,
  FileCheck,
  FileText,
  GraduationCap,
  Users,
  CheckCircle2,
  XCircle,
  Archive,
  type LucideIcon,
} from 'lucide-react';
import { DetailsCard } from '@/components/common/DetailsCard';
import { LabelBadge } from '@/components/common/LabelBadge';
import { Spinner } from '@/components/ui/spinner';
import { useQuery } from '@tanstack/react-query';
import { getUserHistory } from '@/services/history.service';
import type {
  HistoryTimelineRole,
  UserHistoryDocument,
  UserHistoryOnboardingStep,
  UserHistoryValidation,
} from '@/types/history.types';
import { getErrorMessage } from '@/utils/errors-utils';
import { getRoleLabel, roleKind } from '@/utils/role-label-utils';

/** Props for {@link UserHistoryTimeline}. */
interface UserHistoryTimelineProps {
  userId: string;
  role: HistoryTimelineRole;
}

/** One timeline entry (date, label, optional reason/actor). */
interface TimelineEvent {
  date: Date;
  label: string;
  reason?: string;
  icon: LucideIcon;
  iconColor: string;
  badge?: 'created' | 'updated';
  actorName?: string;
}

/** Events sharing the same local-timezone day. */
interface DayGroup {
  dayKey: string;
  dayLabel: string;
  events: TimelineEvent[];
}

/**
 * Map onboardingStatus values to human-readable labels
 */
const ONBOARDING_STATUS_LABELS: Record<string, string> = {
  prospect: "Inscription de l'utilisateur",
  lead_qualifie: 'Promotion en Lead qualifié',
  lead_a_valider: 'Lead à valider',
  lead_affectable: 'Lead affectable',
  lead_programmable: 'Lead programmable',
  lead_parcours: 'Lead parcours',
  lead_sensibilise: 'Lead sensibilisé',
  lead_certifie: 'Lead certifié',
  lead_inscrit: 'Lead inscrit',
  lead_confirme: 'Lead confirmé',
  tuteur_a_affecter: "En attente d'élève",
  eleve_a_affecter: 'En attente tuteur',
};

/**
 * Map cycleStep values to human-readable labels
 */
const CYCLE_STEP_LABELS: Record<string, string> = {
  acquisition: 'Promotion en Lead',
};

/**
 * Map milestone completion fields to labels
 */
const MILESTONE_LABELS: Record<string, { label: string; icon: LucideIcon }> = {
  officialDocumentsCompleted: { label: 'Validation des documents', icon: FileCheck },
  contractCompleted: { label: "Signature du contrat d'engagement", icon: FileText },
  certificationCompleted: { label: 'Formation pédagogique', icon: GraduationCap },
  informationSessionCompleted: { label: "Réunion d'information", icon: Users },
  engagementDataCompleted: { label: "Remplissage de l'engagement", icon: CheckCircle2 },
  onboardingCompleted: { label: 'Onboarding complété', icon: CheckCircle2 },
};

/** Role-specific archive fallback reason. */
function getArchiveReason(role: HistoryTimelineRole): string {
  if (roleKind(role) === 'tutor') return 'Mise à jour des informations du tuteur.';
  if (roleKind(role) === 'student') return "Mise à jour des informations de l'élève.";
  return 'Mise à jour des informations du parent.';
}

/**
 * Build a local-timezone day key (YYYY-MM-DD in local time, not UTC)
 * Fixes timezone mismatch: toISOString() returns UTC, which can shift the day
 * for events near midnight in France (UTC+1/+2).
 */
function localDayKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Format a day label (today / yesterday / DD/MM/YYYY).
 * Uses local time throughout to stay consistent with {@link localDayKey}.
 */
function formatDayLabel(date: Date): string {
  const todayKey = localDayKey(new Date());
  const eventKey = localDayKey(date);

  if (eventKey === todayKey) return "Aujourd'hui";

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  if (eventKey === localDayKey(yesterday)) return 'Hier';

  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/** Format a date as DD/MM/YYYY for the event timestamp. */
function formatShortDate(date: Date): string {
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/** Local-timezone HH:MM for the event timestamp. */
function formatTime(date: Date): string {
  return date.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Build timeline events from user history document.
 * Skip the `prospect` onboardingStep when registrationDate already emitted
 * the registration event (avoids duplicates).
 */
function buildTimelineEvents(
  history: UserHistoryDocument,
  role: HistoryTimelineRole,
): TimelineEvent[] {
  const events: TimelineEvent[] = [];
  const { registrationAndOnboarding, archive } = history;

  const hasRegistrationEvent = !!registrationAndOnboarding.registrationDate;
  if (hasRegistrationEvent && registrationAndOnboarding.registrationDate) {
    events.push({
      date: new Date(registrationAndOnboarding.registrationDate),
      label: "Inscription de l'utilisateur",
      icon: User,
      iconColor: 'var(--color-1-nuance-1)',
      badge: 'created',
    });
  }

  registrationAndOnboarding.onboardingSteps.forEach((step: UserHistoryOnboardingStep) => {
    const date = new Date(step.changedAt);
    const actorName = step.changedBy?.userFullName;

    if (step.stepType === 'onBoardingStatus' && step.newValue) {
      // Skip 'prospect' if registrationDate already generated the registration event
      // (avoids showing the user-registration event twice)
      if (step.newValue === 'prospect' && hasRegistrationEvent) return;

      const label = ONBOARDING_STATUS_LABELS[step.newValue] ?? `Statut : ${step.newValue}`;
      events.push({
        date,
        label,
        reason: step.changedBy?.reason ?? undefined,
        icon: TrendingUp,
        iconColor: 'var(--color-1-nuance-1)',
        badge: step.oldValue === null ? 'created' : 'updated',
        actorName,
      });
    }

    if (step.stepType === 'cycleStep' && step.newValue) {
      const label = CYCLE_STEP_LABELS[step.newValue] ?? `Cycle : ${step.newValue}`;
      events.push({
        date,
        label,
        reason: step.changedBy?.reason ?? undefined,
        icon: RefreshCw,
        iconColor: 'var(--color-1-nuance-1)',
        actorName,
      });
    }

    if (step.stepType === 'folderSituation' && step.newValue) {
      events.push({
        date,
        label: `Changement situation dossier : ${step.newValue}`,
        reason: step.changedBy?.reason ?? undefined,
        icon: FolderOpen,
        iconColor: 'var(--color-1-nuance-1)',
        actorName,
      });
    }
  });

  Object.entries(MILESTONE_LABELS).forEach(([field, config]) => {
    const ro = registrationAndOnboarding as unknown as Record<string, string | null | undefined>;
    const completionDate = ro[field];
    if (completionDate) {
      events.push({
        date: new Date(completionDate),
        label: config.label,
        icon: config.icon,
        iconColor: 'var(--color-1-nuance-1)',
      });
    }
  });

  registrationAndOnboarding.validations.forEach((validation: UserHistoryValidation) => {
    const date = new Date(validation.validatedAt);
    if (validation.isValidated) {
      events.push({
        date,
        label: 'Dossier validé',
        reason: validation.validationComments ?? undefined,
        icon: CheckCircle2,
        iconColor: 'var(--color-1-nuance-1)',
        actorName: validation.validatedBy?.userFullName,
      });
    } else {
      events.push({
        date,
        label: 'Dossier refusé',
        reason: validation.rejectionReason ?? validation.validationComments ?? undefined,
        icon: XCircle,
        iconColor: '#EF4444',
        actorName: validation.validatedBy?.userFullName,
      });
    }
  });

  /** Archive reason falls back to a role-specific default. */
  if (archive?.archivedAt) {
    let roleLabel = 'Utilisateur';
    if (roleKind(role) === 'tutor') {
      roleLabel = getRoleLabel('tutor');
    } else if (roleKind(role) === 'student') {
      roleLabel = getRoleLabel('student');
    } else if (roleKind(role) === 'parent') {
      roleLabel = getRoleLabel('parent');
    }

    events.push({
      date: new Date(archive.archivedAt),
      label: `Statut : ${roleLabel} archivé`,
      reason: archive.comment
        ? `${archive.reason ?? getArchiveReason(role)} — ${archive.comment}`
        : (archive.reason ?? getArchiveReason(role)),
      icon: Archive,
      iconColor: 'var(--color-1-nuance-1)',
      actorName: archive.archivedBy?.userFullName,
    });
  }

  return events.sort((a, b) => b.date.getTime() - a.date.getTime());
}

/**
 * Group events by local-timezone day.
 */
function groupEventsByDay(events: TimelineEvent[]): DayGroup[] {
  const groups = new Map<string, TimelineEvent[]>();

  events.forEach((event) => {
    const key = localDayKey(event.date);
    const existing = groups.get(key);
    if (existing) {
      existing.push(event);
    } else {
      groups.set(key, [event]);
    }
  });

  return Array.from(groups.entries()).map(([key, dayEvents]) => ({
    dayKey: key,
    dayLabel: formatDayLabel(dayEvents[0].date),
    events: dayEvents,
  }));
}

/** Timeline of archive, status, and role events for a user. */
export const UserHistoryTimeline: React.FC<UserHistoryTimelineProps> = ({ userId, role }) => {
  const {
    data: history,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['history', 'user', userId],
    queryFn: () => getUserHistory(userId),
    enabled: !!userId,
  });

  if (isLoading) {
    return (
      <div className="details-view-grid mt-6">
        <DetailsCard icon={Clock} title="Historique" colorName="color-2" colSpan={2}>
          <div className="flex flex-col items-center justify-center py-12">
            <Spinner size="md" />
            <p className="mt-3 text-sm text-muted-foreground">Chargement de l'historique…</p>
          </div>
        </DetailsCard>
      </div>
    );
  }

  if (error) {
    return (
      <div className="details-view-grid mt-6">
        <DetailsCard icon={Clock} title="Historique" colorName="color-2" colSpan={2}>
          <div className="flex flex-col items-center justify-center py-12">
            <XCircle className="w-12 h-12 text-red-500 mb-3" />
            <p className="text-sm text-red-600">{getErrorMessage(error ?? 'loadHistoryFailed')}</p>
          </div>
        </DetailsCard>
      </div>
    );
  }

  if (!history) {
    return (
      <div className="details-view-grid mt-6">
        <DetailsCard icon={Clock} title="Historique" colorName="color-2" colSpan={2}>
          <div className="flex flex-col items-center justify-center py-12">
            <p className="text-sm text-muted-foreground">Aucune donnée disponible</p>
          </div>
        </DetailsCard>
      </div>
    );
  }

  const events = buildTimelineEvents(history, role);

  if (events.length === 0) {
    return (
      <div className="details-view-grid mt-6">
        <DetailsCard icon={Clock} title="Historique" colorName="color-2" colSpan={2}>
          <div className="flex flex-col items-center justify-center py-12">
            <Clock className="w-12 h-12 text-muted-foreground mb-3" />
            <p className="text-sm font-medium text-foreground">
              Pas d'historique pour cet utilisateur
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Vérifiez les filtres ou réessayez plus tard.
            </p>
          </div>
        </DetailsCard>
      </div>
    );
  }

  const dayGroups = groupEventsByDay(events);

  return (
    <div className="details-view-grid mt-6">
      <DetailsCard icon={Clock} title="Historique" colorName="color-2" colSpan={2}>
        <div className="max-h-[520px] overflow-y-auto space-y-4 pr-1">
          {dayGroups.map((group) => (
            <div key={group.dayKey}>
              <div className="flex items-center gap-2 mb-3">
                <CalendarDays className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
                <span className="text-xs font-semibold text-foreground uppercase tracking-wide">
                  {group.dayLabel}
                </span>
              </div>

              <div className="border-l-2 border-gray-200 pl-4 space-y-3 ml-2">
                {group.events.map((event, eventIdx) => {
                  const Icon = event.icon;
                  const eventKey = `${event.date.getTime()}-${event.label}-${eventIdx}`;
                  return (
                    <div key={eventKey} className="flex items-start gap-3">
                      <div
                        className="w-7 h-7 rounded-full bg-orange-50 border border-orange-200
                                   flex items-center justify-center flex-shrink-0 -ml-[19px]"
                      >
                        <Icon className="w-3.5 h-3.5" style={{ color: event.iconColor }} />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-medium text-foreground">
                                {event.label}
                              </span>
                              {event.badge === 'created' && (
                                <LabelBadge
                                  label="Créé"
                                  variant="active"
                                  color="#10B981"
                                  size="sm"
                                />
                              )}
                              {event.badge === 'updated' && (
                                <LabelBadge
                                  label="Mise à jour"
                                  variant="outlined"
                                  color="#6366F1"
                                  size="sm"
                                />
                              )}
                            </div>
                            {event.reason && (
                              <p className="text-xs text-muted-foreground mt-1">{event.reason}</p>
                            )}
                          </div>

                          <div className="text-right shrink-0 ml-4">
                            {event.actorName && (
                              <p className="text-xs text-muted-foreground">{event.actorName}</p>
                            )}
                            <p className="text-xs text-muted-foreground">
                              {formatShortDate(event.date)}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {formatTime(event.date)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </DetailsCard>
    </div>
  );
};
